import React from 'react';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { PlayArrowRounded } from '@mui/icons-material';
import { TTS_VERSION } from '@src/constants/tts';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import route from '@src/constants/route';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import Tooltip from '@src/components/Tooltip';
import { capitalize } from 'lodash';
import { VOICE_LEVEL } from '@src/constants/voice';
import actions from '@src/redux/actions';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import {
  FEATURE,
  PACKAGE_CODE_V3,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { checkVietNam } from '@src/utils/checkCountry';
import { checkFeaturePermission } from '@src/services/tts';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Typography } from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import useUserInfo from '@src/hooks/useTtsUser';
import { canUseEOLVoice } from '@src/services/voice';
import CrownImg from '@src/assets/images/crown.png';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledVoice } from './index.styled';

const Voice = ({
  voice,
  onSelectedVoice,
  usingPackageType,
  onChangePlayAudio,
  active,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { openSentences } = useSelector((state) => state.synthesisRequest);
  const { ttsUser } = useUserInfo();
  const { usingPackage, usingDubbingPackage } = useSelector(
    (state) => state.user,
  );
  const { defaultGlobalVoice } = useSelector((state) => state.voice);

  const isVietNam = checkVietNam();
  const isDefaultGlobalVoice =
    !isVietNam &&
    usingPackage?.level === PACKAGE_LEVEL.BASIC &&
    voice?.code === defaultGlobalVoice;

  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();

  const useUserEolDate = getFeatureValue(
    FEATURE_KEYS.UPDATE_VOICES_BY_USER_EOL_DATE,
    {
      userId: ttsUser.id,
    },
  );

  const canUseVoice = () => {
    if (!voice?.features?.length) return true;

    const usingPackageByType =
      usingPackageType === PACKAGE_TYPE.DUBBING ? ttsUser?.dubbing : ttsUser;

    if (!usingDubbingPackage && isDefaultGlobalVoice) return true;

    const eolVoice = voice?.eolDate;
    if (eolVoice && useUserEolDate && canUseEOLVoice({ voice, user: ttsUser }))
      return true;

    return voice.features.every((item) =>
      checkFeaturePermission(usingPackageByType?.features, item),
    );
  };

  const handleLimitedFeatureVoiceDialog = () => {
    const isBasicGlobalVoice = voice?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isAdvancedGlobalVoice = voice?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    if (isAdvancedGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(
              `${route.PAYMENT}?package=${PACKAGE_CODE_V3.STUDIO_VIP_YEAR_V3}`,
            );
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );

      return;
    }

    if (isBasicGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeSpecialOrVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
      }),
    );
  };

  const handleChangePlayAudio = (e) => {
    e.stopPropagation();
    onChangePlayAudio(voice);
  };

  const handleCloseNotificationDialog = () =>
    dispatch(
      actions.dialog.displayDialog({
        notificationDialog: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const handleSelectedVoice = () => {
    if (voice?.version === TTS_VERSION.EMPHASIS && openSentences) {
      dispatch(
        actions.dialog.displayDialog({
          notificationDialog: {
            name: 'cannotUseVoice',
            title: t('cannotUseVoice'),
            description: t('cannotUseVoiceDes'),
            variant: 'warning',
            open: true,
            actionComponent: (
              <Button
                variant="contained"
                onClick={handleCloseNotificationDialog}
              >
                {t('understood')}
              </Button>
            ),
          },
        }),
      );
      return;
    }

    if (canUseVoice()) {
      onSelectedVoice(voice);
    } else {
      handleLimitedFeatureVoiceDialog();
    }
  };

  const isAboutToBeRemoved = voice?.eolDate
    ? moment().isBefore(voice?.eolDate)
    : false;

  const daysLeftBeforeVoiceInactive = moment(voice?.eolDate).diff(
    moment(),
    'days',
  );

  return (
    <StyledVoice onClick={handleSelectedVoice}>
      <BadgeAvatar
        width={40}
        smallImgWidth={10}
        img={voice && voice.roundImage}
        smallImg={voice && voice.language && voice.language.roundImage}
        type="image"
        activeMobileWeb={active}
      />
      <div className="content">
        <span className="voice-name">
          {voice.name}
          {voice?.level === VOICE_LEVEL.ADVANCED && voice?.creditFactor > 1 && (
            <span className="x2-char-tag">{`x${voice?.creditFactor}`}</span>
          )}
        </span>
        <div className="more-info-voice-box">
          {voice?.level !== VOICE_LEVEL.BASIC && (
            <div className="tag-voice">{t(voice?.level)}</div>
          )}
          {voice?.beta && (
            <div className="tag-voice-beta">{capitalize(t('beta'))}</div>
          )}

          {isAboutToBeRemoved && (
            <Typography className="eol-tag-name">
              <Tooltip
                arrow
                title={t('discontinuedAfterTimeOnMobile', {
                  date: moment(voice?.eolDate)?.format('DD/MM/YYYY'),
                })}
                placement="top"
              >
                <RunningWithErrorsRoundedIcon className="returning-error-icon" />
              </Tooltip>
              {t(daysLeftBeforeVoiceInactive ? 'dayNumber' : 'today', {
                daysLeftBeforeVoiceInactive,
              })}
            </Typography>
          )}
        </div>
        {isAboutToBeRemoved && (active || voice.playStatus) && (
          <div className="discontinued-title">
            <RunningWithErrorsRoundedIcon className="returning-error-icon" />
            {t('discontinuedAfterTimeOnMobile', {
              date: moment(voice?.eolDate)?.format('DD/MM/YYYY'),
            })}
          </div>
        )}
        {/* <span className="voice-infor"> HN - Nữ - Giải trí</span> */}
      </div>
      <IconButton
        onClick={handleChangePlayAudio}
        aria-label="play"
        className="icon-button"
      >
        {voice.playStatus ? (
          <PauseRoundedIcon className="play-icon" fontSize="large" />
        ) : (
          <PlayArrowRounded className="play-icon" fontSize="large" />
        )}
      </IconButton>
    </StyledVoice>
  );
};

export default Voice;
